import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import DataTable, { Column } from '@/components/ui/data-table';
import toast from 'react-hot-toast';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';
import {
    ArrowLeft,
    Building,
    Users,
    Settings,
    Save,
    Power,
    PowerOff,
    Edit,
    Trash2,
    UserCheck,
    Key,
} from 'lucide-react';
import httpClient from '@/api/httpClient';
import { useStartUserImpersonation } from '@/api/impersonation/use-impersonation';
import { CustomDomainInfo, CustomDomainTlsPanel } from '@/components/admin/CustomDomainStatus';

interface Tenant extends CustomDomainInfo {
    id: string;
    tenant_slug: string;
    tenant_name: string;
    system_name: string;
    custom_domain?: string;
    is_active: boolean;
    is_admin_tenant: boolean;
    support_email: string;
    support_phone?: string;
    support_address?: string;
    trading_enabled: boolean;
    registration_enabled: boolean;
    maintenance_mode: boolean;
    maintenance_message?: string;
    default_currency: string;
    timezone: string;
    date_format: string;
    time_format: string;
    max_login_attempts: number;
    session_timeout_minutes: number;
    created_at: string;
    updated_at: string;
}

interface TenantUser {
    id: string;
    name: string;
    email: string;
    status: string;
    is_tenant_manager: boolean;
    created_at: string;
    last_login_at?: string;
}

export function TenantDetailsPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [tenant, setTenant] = useState<Tenant | null>(null);
    const [users, setUsers] = useState<TenantUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [editingField, setEditingField] = useState<string | null>(null);
    const [tempValue, setTempValue] = useState<string>('');
    const [userFilters, setUserFilters] = useState<Record<string, any>>({});
    const [userSortConfig, setUserSortConfig] = useState<{
        key: keyof TenantUser;
        direction: 'asc' | 'desc';
    }>({ key: 'name', direction: 'asc' });

    // Impersonation
    const startUserImpersonationMutation = useStartUserImpersonation();
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);

    // Password reset
    const [isPasswordResetDialogOpen, setIsPasswordResetDialogOpen] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isResettingPassword, setIsResettingPassword] = useState(false);

    // Fetch tenant details
    const fetchTenant = async () => {
        try {
            const response = await httpClient.get(`/tenant-manage/tenants/${id}`);
            const data = response.data;
            if (data.success) {
                setTenant(data.data);
            } else {
                // Handle API error response
                throw new Error(data.message || 'Failed to fetch tenant');
            }
        } catch (error: any) {
            console.error('Failed to fetch tenant:', error);

            // Check if it's a 404 error (tenant not found)
            if (error.response?.status === 404) {
                // Don't show toast for 404 - let the UI handle it gracefully
                setTenant(null);
            } else {
                // Show toast for other errors
                toast.error('Failed to fetch tenant details');
            }
        }
    };

    // Fetch tenant users
    const fetchUsers = async () => {
        try {
            const response = await httpClient.get(`/tenant-manage/tenants/${id}/users`);
            const data = response.data;
            if (data.success) {
                setUsers(data.data.users);
            }
        } catch (error) {
            console.error('Failed to fetch tenant users:', error);
            toast.error('Failed to fetch tenant users');
        }
    };

    // Start editing a field
    const startEditing = (field: string, currentValue: string) => {
        setEditingField(field);
        setTempValue(currentValue);
    };

    // Cancel editing
    const cancelEditing = () => {
        setEditingField(null);
        setTempValue('');
    };

    // Save a single field
    const saveField = async (field: string) => {
        if (!tenant) return;

        setIsSaving(true);
        try {
            const updateData = { [field]: tempValue };
            const response = await httpClient.put(
                `/tenant-manage/tenants/${tenant.id}`,
                updateData
            );
            const data = response.data;
            if (data.success) {
                setTenant(data.data);
                setEditingField(null);
                setTempValue('');
                toast.success('Field updated successfully');
            }
        } catch (error) {
            console.error('Failed to update field:', error);
            toast.error('Failed to update field');
        } finally {
            setIsSaving(false);
        }
    };

    // Toggle boolean fields
    const toggleBooleanField = async (field: string, currentValue: boolean) => {
        if (!tenant) return;

        setIsSaving(true);
        try {
            const updateData = { [field]: !currentValue };
            const response = await httpClient.put(
                `/tenant-manage/tenants/${tenant.id}`,
                updateData
            );
            const data = response.data;
            if (data.success) {
                setTenant(data.data);
                toast.success(`${field.replace('_', ' ')} updated successfully`);
            }
        } catch (error) {
            console.error('Failed to update field:', error);
            toast.error('Failed to update field');
        } finally {
            setIsSaving(false);
        }
    };

    // Toggle tenant status
    const toggleTenantStatus = async () => {
        if (!tenant) return;

        try {
            const endpoint = tenant.is_active
                ? `/tenant-manage/tenants/${tenant.id}/deactivate`
                : `/tenant-manage/tenants/${tenant.id}/activate`;

            const response = await httpClient.post(endpoint);
            const data = response.data;
            if (data.success) {
                setTenant(prev => (prev ? { ...prev, is_active: !prev.is_active } : null));
                toast.success(
                    `Tenant ${tenant.is_active ? 'deactivated' : 'activated'} successfully`
                );
            }
        } catch (error) {
            console.error('Failed to toggle tenant status:', error);
            toast.error('Failed to toggle tenant status');
        }
    };

    // Delete tenant
    const deleteTenant = async () => {
        if (!tenant || tenant.is_admin_tenant) return;

        if (
            !confirm('Are you sure you want to delete this tenant? This action cannot be undone.')
        ) {
            return;
        }

        try {
            const response = await httpClient.delete(`/tenant-manage/tenants/${tenant.id}`);
            const data = response.data;
            if (data.success) {
                toast.success('Tenant deleted successfully');
                navigate('/tenants');
            }
        } catch (error) {
            console.error('Failed to delete tenant:', error);
            toast.error('Failed to delete tenant');
        }
    };

    // Reset tenant admin password
    const resetAdminPassword = async () => {
        if (!tenant || tenant.is_admin_tenant) return;

        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }

        if (newPassword.length < 8) {
            toast.error('Password must be at least 8 characters long');
            return;
        }

        setIsResettingPassword(true);

        try {
            const response = await httpClient.post(
                `/tenant-manage/tenants/${tenant.id}/reset-admin-password`,
                {
                    new_password: newPassword,
                    confirm_password: confirmPassword,
                }
            );

            const data = response.data;
            if (data.success) {
                toast.success('Tenant admin password reset successfully');
                setIsPasswordResetDialogOpen(false);
                setNewPassword('');
                setConfirmPassword('');
            } else {
                toast.error(data.message || 'Failed to reset password');
            }
        } catch (error: any) {
            console.error('Failed to reset admin password:', error);
            const errorMessage = error.response?.data?.message || 'Failed to reset admin password';
            toast.error(errorMessage);
        } finally {
            setIsResettingPassword(false);
        }
    };

    // Impersonate tenant admin user
    const impersonateTenantAdmin = async () => {
        if (!tenant || tenant.is_admin_tenant) return;

        try {
            // Find the tenant admin user (is_tenant_manager = true)
            const adminUser = users.find(user => user.is_tenant_manager);

            if (!adminUser) {
                toast.error('No admin user found for this tenant');
                return;
            }

            if (adminUser.status !== 'active') {
                toast.error('Cannot impersonate inactive user');
                return;
            }

            await startUserImpersonationMutation.mutateAsync({ user_id: adminUser.id });
            toast.success(`Successfully impersonating ${adminUser.name}`);

            // Redirect to CRM subdomain
            const currentHost = window.location.hostname;
            const crmHost = currentHost.replace('admin.', 'crm.');
            window.location.href = `http://${crmHost}/`;
        } catch (error: any) {
            console.error('Failed to impersonate tenant admin:', error);
            toast.error(error.message || 'Failed to impersonate tenant admin');
        }
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            await Promise.all([fetchTenant(), fetchUsers()]);
            setIsLoading(false);
        };

        if (id) {
            loadData();
        }
    }, [id]);

    const userColumns: Column<TenantUser>[] = [
        {
            id: 'name',
            key: 'name',
            label: 'Name',
            sortable: true,
        },
        {
            id: 'email',
            key: 'email',
            label: 'Email',
            sortable: true,
        },
        {
            id: 'status',
            key: 'status',
            label: 'Status',
            render: user => (
                <Badge variant={user?.status === 'active' ? 'default' : 'secondary'}>
                    {user?.status || 'Unknown'}
                </Badge>
            ),
        },
        {
            id: 'is_tenant_manager',
            key: 'is_tenant_manager',
            label: 'Role',
            render: user => (
                <Badge variant={user?.is_tenant_manager ? 'default' : 'outline'}>
                    {user?.is_tenant_manager ? 'Manager' : 'User'}
                </Badge>
            ),
        },
        {
            id: 'last_login_at',
            key: 'last_login_at',
            label: 'Last Login',
            render: user =>
                user?.last_login_at ? <DateTimeFormatter date={user.last_login_at} /> : 'Never',
        },
    ];

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!tenant) {
        return (
            <div className="flex flex-col items-center justify-center min-h-96 space-y-4">
                <div className="text-center">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <Building className="h-8 w-8 text-gray-400" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Tenant Not Found</h2>
                    <p className="text-gray-600 mb-6">
                        The tenant you're looking for doesn't exist or you don't have permission to
                        view it.
                    </p>
                    <Button onClick={() => navigate('/tenants')}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back to Tenants
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                    <Button variant="outline" onClick={() => navigate('/tenants')}>
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">{tenant.tenant_name}</h1>
                        <p className="text-gray-600">{tenant.system_name}</p>
                    </div>
                </div>
                <div className="flex items-center space-x-2">
                    <Badge variant={tenant.is_active ? 'default' : 'secondary'}>
                        {tenant.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                    {tenant.is_admin_tenant && <Badge variant="outline">Admin Tenant</Badge>}
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
                {!tenant.is_admin_tenant && (
                    <>
                        <Button
                            onClick={toggleTenantStatus}
                            variant={tenant.is_active ? 'destructive' : 'default'}
                        >
                            {tenant.is_active ? (
                                <>
                                    <PowerOff className="h-4 w-4 mr-2" />
                                    Deactivate
                                </>
                            ) : (
                                <>
                                    <Power className="h-4 w-4 mr-2" />
                                    Activate
                                </>
                            )}
                        </Button>

                        <Button onClick={deleteTenant} variant="destructive">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                        </Button>

                        <Button
                            onClick={impersonateTenantAdmin}
                            variant="outline"
                            disabled={startUserImpersonationMutation.isPending}
                        >
                            <UserCheck className="h-4 w-4 mr-2" />
                            {startUserImpersonationMutation.isPending
                                ? 'Impersonating...'
                                : 'Impersonate Admin'}
                        </Button>

                        <Dialog
                            open={isPasswordResetDialogOpen}
                            onOpenChange={setIsPasswordResetDialogOpen}
                        >
                            <DialogTrigger asChild>
                                <Button variant="outline">
                                    <Key className="h-4 w-4 mr-2" />
                                    Reset Admin Password
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="sm:max-w-[425px]">
                                <DialogHeader>
                                    <DialogTitle>Reset Tenant Admin Password</DialogTitle>
                                    <DialogDescription>
                                        Reset the password for the tenant admin user. This will
                                        allow the admin to log in with the new password.
                                    </DialogDescription>
                                </DialogHeader>
                                <div className="space-y-4">
                                    <div>
                                        <Label htmlFor="new-password">New Password</Label>
                                        <Input
                                            id="new-password"
                                            type="password"
                                            value={newPassword}
                                            onChange={e => setNewPassword(e.target.value)}
                                            placeholder="Enter new password (min 8 characters)"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label htmlFor="confirm-password">Confirm Password</Label>
                                        <Input
                                            id="confirm-password"
                                            type="password"
                                            value={confirmPassword}
                                            onChange={e => setConfirmPassword(e.target.value)}
                                            placeholder="Confirm new password"
                                            required
                                        />
                                    </div>
                                    <div className="flex justify-end space-x-2">
                                        <Button
                                            variant="outline"
                                            onClick={() => {
                                                setIsPasswordResetDialogOpen(false);
                                                setNewPassword('');
                                                setConfirmPassword('');
                                            }}
                                            disabled={isResettingPassword}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            onClick={resetAdminPassword}
                                            disabled={
                                                isResettingPassword ||
                                                !newPassword ||
                                                !confirmPassword
                                            }
                                        >
                                            {isResettingPassword
                                                ? 'Resetting...'
                                                : 'Reset Password'}
                                        </Button>
                                    </div>
                                </div>
                            </DialogContent>
                        </Dialog>
                    </>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Tenant Details */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <Building className="h-5 w-5 mr-2" />
                            Tenant Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {/* Tenant Name */}
                        <div>
                            <Label className="text-sm font-medium text-gray-500">Tenant Name</Label>
                            {editingField === 'tenant_name' ? (
                                <div className="flex items-center space-x-2 mt-1">
                                    <Input
                                        value={tempValue}
                                        onChange={e => setTempValue(e.target.value)}
                                        className="flex-1"
                                    />
                                    <Button
                                        size="sm"
                                        onClick={() => saveField('tenant_name')}
                                        disabled={isSaving}
                                    >
                                        <Save className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={cancelEditing}>
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2 mt-1">
                                    <p className="text-lg flex-1">{tenant.tenant_name}</p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                            startEditing('tenant_name', tenant.tenant_name)
                                        }
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* System Name */}
                        <div>
                            <Label className="text-sm font-medium text-gray-500">System Name</Label>
                            {editingField === 'system_name' ? (
                                <div className="flex items-center space-x-2 mt-1">
                                    <Input
                                        value={tempValue}
                                        onChange={e => setTempValue(e.target.value)}
                                        className="flex-1"
                                    />
                                    <Button
                                        size="sm"
                                        onClick={() => saveField('system_name')}
                                        disabled={isSaving}
                                    >
                                        <Save className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={cancelEditing}>
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2 mt-1">
                                    <p className="text-lg flex-1">{tenant.system_name}</p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                            startEditing('system_name', tenant.system_name)
                                        }
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Tenant Slug */}
                        <div>
                            <Label className="text-sm font-medium text-gray-500">Tenant Slug</Label>
                            <p className="text-lg font-mono">{tenant.tenant_slug}</p>
                        </div>

                        {/* Custom Domain */}
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Custom Domain
                            </Label>
                            {editingField === 'custom_domain' ? (
                                <div className="flex items-center space-x-2 mt-1">
                                    <Input
                                        value={tempValue}
                                        onChange={e => setTempValue(e.target.value)}
                                        className="flex-1"
                                        placeholder="example.com"
                                    />
                                    <Button
                                        size="sm"
                                        onClick={() => saveField('custom_domain')}
                                        disabled={isSaving}
                                    >
                                        <Save className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={cancelEditing}>
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2 mt-1">
                                    <p className="text-lg flex-1">
                                        {tenant.custom_domain || 'Not set'}
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                            startEditing(
                                                'custom_domain',
                                                tenant.custom_domain || ''
                                            )
                                        }
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                            <CustomDomainTlsPanel
                                tenant={tenant}
                                onUpdated={updated =>
                                    setTenant(prev => (prev ? { ...prev, ...updated } : prev))
                                }
                            />
                        </div>

                        {/* Support Email */}
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Support Email
                            </Label>
                            {editingField === 'support_email' ? (
                                <div className="flex items-center space-x-2 mt-1">
                                    <Input
                                        type="email"
                                        value={tempValue}
                                        onChange={e => setTempValue(e.target.value)}
                                        className="flex-1"
                                    />
                                    <Button
                                        size="sm"
                                        onClick={() => saveField('support_email')}
                                        disabled={isSaving}
                                    >
                                        <Save className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={cancelEditing}>
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2 mt-1">
                                    <p className="text-lg flex-1">{tenant.support_email}</p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                            startEditing('support_email', tenant.support_email)
                                        }
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Support Phone */}
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Support Phone
                            </Label>
                            {editingField === 'support_phone' ? (
                                <div className="flex items-center space-x-2 mt-1">
                                    <Input
                                        value={tempValue}
                                        onChange={e => setTempValue(e.target.value)}
                                        className="flex-1"
                                    />
                                    <Button
                                        size="sm"
                                        onClick={() => saveField('support_phone')}
                                        disabled={isSaving}
                                    >
                                        <Save className="h-4 w-4" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={cancelEditing}>
                                        Cancel
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex items-center space-x-2 mt-1">
                                    <p className="text-lg flex-1">
                                        {tenant.support_phone || 'Not set'}
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                            startEditing(
                                                'support_phone',
                                                tenant.support_phone || ''
                                            )
                                        }
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Support Address */}
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Support Address
                            </Label>
                            {editingField === 'support_address' ? (
                                <div className="flex items-start space-x-2 mt-1">
                                    <Textarea
                                        value={tempValue}
                                        onChange={e => setTempValue(e.target.value)}
                                        className="flex-1"
                                        rows={3}
                                    />
                                    <div className="flex flex-col space-y-1">
                                        <Button
                                            size="sm"
                                            onClick={() => saveField('support_address')}
                                            disabled={isSaving}
                                        >
                                            <Save className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={cancelEditing}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start space-x-2 mt-1">
                                    <p className="text-lg flex-1 whitespace-pre-line">
                                        {tenant.support_address || 'Not set'}
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                            startEditing(
                                                'support_address',
                                                tenant.support_address || ''
                                            )
                                        }
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Maintenance Message */}
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Maintenance Message
                            </Label>
                            {editingField === 'maintenance_message' ? (
                                <div className="flex items-start space-x-2 mt-1">
                                    <Textarea
                                        value={tempValue}
                                        onChange={e => setTempValue(e.target.value)}
                                        className="flex-1"
                                        rows={2}
                                        placeholder="System is under maintenance..."
                                    />
                                    <div className="flex flex-col space-y-1">
                                        <Button
                                            size="sm"
                                            onClick={() => saveField('maintenance_message')}
                                            disabled={isSaving}
                                        >
                                            <Save className="h-4 w-4" />
                                        </Button>
                                        <Button size="sm" variant="outline" onClick={cancelEditing}>
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-start space-x-2 mt-1">
                                    <p className="text-lg flex-1 whitespace-pre-line">
                                        {tenant.maintenance_message || 'Not set'}
                                    </p>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() =>
                                            startEditing(
                                                'maintenance_message',
                                                tenant.maintenance_message || ''
                                            )
                                        }
                                    >
                                        <Edit className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>

                        {/* Toggle Fields */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="text-sm font-medium text-gray-500">
                                        Trading
                                    </Label>
                                    <Badge
                                        variant={tenant.trading_enabled ? 'default' : 'secondary'}
                                    >
                                        {tenant.trading_enabled ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                </div>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                        toggleBooleanField(
                                            'trading_enabled',
                                            tenant.trading_enabled
                                        )
                                    }
                                    disabled={isSaving}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </div>
                            <div className="flex items-center justify-between">
                                <div>
                                    <Label className="text-sm font-medium text-gray-500">
                                        Registration
                                    </Label>
                                    <Badge
                                        variant={
                                            tenant.registration_enabled ? 'default' : 'secondary'
                                        }
                                    >
                                        {tenant.registration_enabled ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                </div>
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                        toggleBooleanField(
                                            'registration_enabled',
                                            tenant.registration_enabled
                                        )
                                    }
                                    disabled={isSaving}
                                >
                                    <Edit className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div>
                                <Label className="text-sm font-medium text-gray-500">
                                    Maintenance Mode
                                </Label>
                                <Badge
                                    variant={tenant.maintenance_mode ? 'destructive' : 'default'}
                                >
                                    {tenant.maintenance_mode ? 'Active' : 'Inactive'}
                                </Badge>
                            </div>
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={() =>
                                    toggleBooleanField('maintenance_mode', tenant.maintenance_mode)
                                }
                                disabled={isSaving}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                {/* System Settings */}
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center">
                            <Settings className="h-5 w-5 mr-2" />
                            System Settings
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Default Currency
                            </Label>
                            <p className="text-lg">{tenant.default_currency}</p>
                        </div>
                        <div>
                            <Label className="text-sm font-medium text-gray-500">Timezone</Label>
                            <p className="text-lg">{tenant.timezone}</p>
                        </div>
                        <div>
                            <Label className="text-sm font-medium text-gray-500">Date Format</Label>
                            <p className="text-lg">{tenant.date_format}</p>
                        </div>
                        <div>
                            <Label className="text-sm font-medium text-gray-500">Time Format</Label>
                            <p className="text-lg">{tenant.time_format}</p>
                        </div>
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Max Login Attempts
                            </Label>
                            <p className="text-lg">{tenant.max_login_attempts}</p>
                        </div>
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Session Timeout
                            </Label>
                            <p className="text-lg">{tenant.session_timeout_minutes} minutes</p>
                        </div>
                        <div>
                            <Label className="text-sm font-medium text-gray-500">Created</Label>
                            <p className="text-lg">
                                <DateTimeFormatter date={tenant.created_at} />
                            </p>
                        </div>
                        <div>
                            <Label className="text-sm font-medium text-gray-500">
                                Last Updated
                            </Label>
                            <p className="text-lg">
                                <DateTimeFormatter date={tenant.updated_at} />
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Users List */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center">
                        <Users className="h-5 w-5 mr-2" />
                        Users ({users.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <DataTable
                        data={users.filter(user => user !== null)}
                        columns={userColumns}
                        isLoading={false}
                        filters={userFilters}
                        onFilterChange={setUserFilters}
                        sortConfig={userSortConfig}
                        onSort={key =>
                            setUserSortConfig(prev => ({
                                key,
                                direction:
                                    prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
                            }))
                        }
                        selectedItems={selectedUsers}
                        onSelectionChange={setSelectedUsers}
                        getItemId={item => item.id}
                        emptyMessage="No users found"
                        loadingMessage="Loading users..."
                    />
                </CardContent>
            </Card>
        </div>
    );
}
